import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@core/environments/environment';
import {
  AdminPermissionResponse,
  AllRolesResponse,
  CreateRoleRequest,
  PermissionResponse,
  SingleRoleResponse,
} from '@core/models/admin/roles-and-permissions';
import { Pagination } from '@core/models/pagination.model';
import { Observable } from 'rxjs';
import { LoadingService } from './loading.service';
import { finalize } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { AuthSelectors } from 'app/auth/store/auth.selectors';

@Injectable({
  providedIn: 'root',
})
export class RolesAndPermissionsService {
  http = inject(HttpClient);
  private loadingService = inject(LoadingService);
  private store = inject(Store);

  BASE_URL = environment.ADMIN_BASE_URL + '/v1/role';

  getAllPermissions(): Observable<PermissionResponse> {
    return this.http.get<PermissionResponse>(`${this.BASE_URL}/get-permission`);
  }

  filterAllRoles(queryParams: string): Observable<AllRolesResponse> {
    return this.http.get<AllRolesResponse>(`${this.BASE_URL}/filter-roles?${queryParams}`);
  }
  getAllRoles(): Observable<AllRolesResponse> {
    const uniqueStateUrl = 'get-all-roles';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<AllRolesResponse>(
        `${this.BASE_URL}/get-roles`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getAdminPermissionById(adminId: string): Observable<AdminPermissionResponse> {
    return this.http.get<AdminPermissionResponse>(
      `${this.BASE_URL}/get-admin-permissions?admin_id=${adminId}`
    );
  }

  getRoleById(roleId: string): Observable<SingleRoleResponse> {
    return this.http.get<SingleRoleResponse>(
      `${this.BASE_URL}/get-role-by-id?id=${roleId}`
    );
  }

  updateRoleName(updatePayload: {
    name: string;
    role_id: string;
  }): Observable<unknown> {
    return this.http.post(`${this.BASE_URL}/update-roles`, updatePayload);
  }

  addPermissionToRole(payload: { permission_id: string; role_id: string }) {
    return this.http.post(`${this.BASE_URL}/add-permission-to-role`, payload);
  }

  removePermissionFromRole(permissionId: string, roleId?: string) {
    return this.http.post(
      `${this.BASE_URL}/remove-permission-from-role?permission_id=${permissionId}&role_id=${roleId}`,
      {}
    );
  }

  createNewRole(payload: CreateRoleRequest) {
    return this.http.post(`${this.BASE_URL}/create-role`, payload);
  }

  deleteRole(roleId: string) {
    return this.http.delete(`${this.BASE_URL}/delete-role?id=${roleId}`);
  }

  hasPermission(permissionName: string | string[]): boolean {
    const userPermissions = this.store.selectSnapshot(AuthSelectors.getPermissions);
    const requiredPermissions = Array.isArray(permissionName)
      ? permissionName
      : permissionName.split(/\s+/).filter(Boolean);

    return requiredPermissions.some((perm) =>
      userPermissions.some((userPerm: Record<string, string>) => Object.keys(userPerm)[0] === perm)
    );
  }
}
